import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ArrowDownCircle, ArrowUpCircle, History, ChevronRight, Plus, AlertCircle, ChevronLeft} from 'lucide-react';
import { useInvestments } from '../hooks/useInvestments';
import { useTransactions } from '../hooks/useTransactions';
import { formatCurrency } from '../utils/helpers';
import { INVESTMENT_TYPES } from '../utils/constants';

export default function Investments() {
  const navigate = useNavigate();
  const { investments, loading, addInvestment, deleteInvestment, getTotalBalance, fetchInvestments } = useInvestments();
  const { getInvestmentTransactions, deposit, withdraw } = useTransactions();

  const [activeView, setActiveView] = useState('list'); // 'list', 'add', 'topup', 'withdraw'
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDate, setWithdrawDate] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpDate, setTopUpDate] = useState('');
  const [newInvestment, setNewInvestment] = useState({ type: '', name: '', amount: '' });

  const [addError, setAddError] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [topUpError, setTopUpError] = useState('');

  const handleAddInvestment = async () => {
    setAddError('');
    
    if (!newInvestment.type || !newInvestment.name || !newInvestment.amount) {
      setAddError('Please fill all fields');
      return;
    }
    
    // Automatically set date to current date
    const currentDate = new Date().toISOString().split('T')[0];
    const investmentData = { ...newInvestment, date: currentDate };
    
    const result = await addInvestment(investmentData);
    if (result.success) {
      setNewInvestment({ type: '', name: '', amount: '' });
      setActiveView('list');
      setAddError('');
    } else {
      setAddError(result.error || 'Failed to add asset');
    }
  };

  const handleDeleteInvestment = async (id) => {
    await deleteInvestment(id);
  };

  const openWithdrawForm = (investment) => {
    setSelectedInvestment(investment);
    setWithdrawAmount('');
    setWithdrawDate(new Date().toISOString().split('T')[0]);
    setWithdrawError('');
    setActiveView('withdraw');
  };

  const openTopUpForm = (investment) => {
    setSelectedInvestment(investment);
    setTopUpAmount('');
    setTopUpDate(new Date().toISOString().split('T')[0]);
    setTopUpError('');
    setActiveView('topup');
  };

  const processWithdrawal = async () => {
    setWithdrawError('');
    
    if (!selectedInvestment || !withdrawAmount || !withdrawDate) {
      setWithdrawError('Please fill all fields');
      return;
    }
    const amount = parseFloat(withdrawAmount);
    if (amount <= 0 || amount > selectedInvestment.amount) {
      setWithdrawError('Invalid withdrawal amount');
      return;
    }
    const result = await withdraw(selectedInvestment.id, amount, withdrawDate, 'Withdrawal');
    if (result.success) {
      setActiveView('list');
      setSelectedInvestment(null);
      setWithdrawAmount('');
      setWithdrawError('');
      await fetchInvestments();
    } else {
      setWithdrawError(result.error || 'Withdrawal failed');
    }
  };

  const processTopUp = async () => {
    setTopUpError('');
    
    if (!selectedInvestment || !topUpAmount || !topUpDate) {
      setTopUpError('Please fill all fields');
      return;
    }
    const amount = parseFloat(topUpAmount);
    if (amount <= 0) {
      setTopUpError('Invalid top-up amount');
      return;
    }
    const result = await deposit(selectedInvestment.id, amount, topUpDate, 'Top-up deposit');
    if (result.success) {
      setActiveView('list');
      setSelectedInvestment(null);
      setTopUpAmount('');
      setTopUpError('');
      await fetchInvestments();
    } else {
      setTopUpError(result.error || 'Deposit failed');
    }
  };

  const viewInvestmentDetails = (investment) => {
    navigate('/transactions', {
      state: {
        filterInvestmentId: investment.id,
        filterInvestmentName: investment.name
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading...</div>
      </div>
    );
  }

  // Add Asset Form View
  if (activeView === 'add') {
    return (
      <div className="min-h-screen bg-[#0b141a]">
        <div className="bg-[#1f2c34] px-4 py-4 lg:px-6 lg:py-5 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => {
              setActiveView('list');
              setAddError('');
            }}
            className="text-[#8696a0] p-1 -ml-1 hover:bg-[#2a3942] rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
          </button>
          <h3 className="text-white text-lg lg:text-2xl font-medium">Add New Asset</h3>
        </div>

        <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-20 space-y-4 lg:space-y-6">
          {addError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3">
              <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm lg:text-base flex-1">{addError}</p>
            </div>
          )}

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Asset Type</label>
            <select
              value={newInvestment.type}
              onChange={(e) => {
                setNewInvestment({ ...newInvestment, type: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            >
              <option value="">Select Type</option>
              {INVESTMENT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Asset Name</label>
            <input
              type="text"
              placeholder="e.g. ICEA Lion"
              value={newInvestment.name}
              onChange={(e) => {
                setNewInvestment({ ...newInvestment, name: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            />
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Initial Amount</label>
            <input
              type="number"
              placeholder="0.00"
              value={newInvestment.amount}
              onChange={(e) => {
                setNewInvestment({ ...newInvestment, amount: e.target.value });
                if (addError) setAddError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            />
          </div>

          <button 
            onClick={handleAddInvestment} 
            className="w-full bg-[#00a884] text-white rounded-lg px-4 py-3 lg:px-6 lg:py-4 text-base lg:text-lg font-medium active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors mt-6"
          >
            Add Asset
          </button>
        </div>
      </div>
    );
  }

  // Top-Up Form View
  if (activeView === 'topup' && selectedInvestment) {
    return (
      <div className="min-h-screen bg-[#0b141a]">
        <div className="bg-[#1f2c34] px-4 py-4 lg:px-6 lg:py-5 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => {
              setActiveView('list');
              setSelectedInvestment(null);
              setTopUpAmount('');
              setTopUpError('');
            }}
            className="text-[#8696a0] p-1 -ml-1 hover:bg-[#2a3942] rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-lg lg:text-2xl font-medium">Top Up</h3>
            <p className="text-[#8696a0] text-sm lg:text-base truncate">{selectedInvestment.name}</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-20 space-y-4 lg:space-y-6">
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Current Balance</p>
            <p className="text-[#00a884] text-xl lg:text-3xl font-medium">{formatCurrency(selectedInvestment.amount || 0)}</p>
          </div>

          {topUpError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3">
              <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm lg:text-base flex-1">{topUpError}</p>
            </div>
          )}

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Amount to Add</label>
            <input
              type="number"
              placeholder="0.00"
              value={topUpAmount}
              onChange={(e) => {
                setTopUpAmount(e.target.value);
                if (topUpError) setTopUpError('');
              }}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-4 lg:px-5 lg:py-5 focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942] text-lg lg:text-2xl"
            />
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Date</label>
            <input
              type="date"
              value={topUpDate}
              onChange={(e) => {
                setTopUpDate(e.target.value);
                if (topUpError) setTopUpError('');
              }}
              className="w-full bg-[#2a3942] text-white rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] border border-[#2a3942]"
            />
          </div>

          {topUpAmount && (
            <div className="bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-4 lg:p-6">
              <p className="text-[#8696a0] text-xs lg:text-sm mb-2">New Balance</p>
              <p className="text-[#00a884] text-2xl lg:text-4xl font-medium">
                {formatCurrency((selectedInvestment.amount || 0) + parseFloat(topUpAmount || 0))}
              </p>
            </div>
          )}

          <button 
            onClick={processTopUp} 
            className="w-full bg-[#00a884] text-white rounded-lg px-4 py-3 lg:px-6 lg:py-4 text-base lg:text-lg font-medium active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors mt-6"
          >
            Confirm Top Up
          </button>
        </div>
      </div>
    );
  }

  // Withdraw Form View
  if (activeView === 'withdraw' && selectedInvestment) {
    return (
      <div className="min-h-screen bg-[#0b141a]">
        <div className="bg-[#1f2c34] px-4 py-4 lg:px-6 lg:py-5 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => {
              setActiveView('list');
              setSelectedInvestment(null);
              setWithdrawAmount('');
              setWithdrawError('');
            }}
            className="text-[#8696a0] p-1 -ml-1 hover:bg-[#2a3942] rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-lg lg:text-2xl font-medium">Withdraw</h3>
            <p className="text-[#8696a0] text-sm lg:text-base truncate">{selectedInvestment.name}</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4 lg:p-8 pb-20 space-y-4 lg:space-y-6">
          <div className="bg-[#1f2c34] rounded-lg p-4 lg:p-6">
            <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Available Balance</p>
            <p className="text-[#00a884] text-xl lg:text-3xl font-medium">{formatCurrency(selectedInvestment.amount || 0)}</p>
          </div>

          {withdrawError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3">
              <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm lg:text-base flex-1">{withdrawError}</p>
            </div>
          )}

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Amount to Withdraw</label>
            <input
              type="number"
              placeholder="0.00"
              value={withdrawAmount}
              onChange={(e) => {
                setWithdrawAmount(e.target.value);
                if (withdrawError) setWithdrawError('');
              }}
              max={selectedInvestment.amount}
              className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-4 py-4 lg:px-5 lg:py-5 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-[#2a3942] text-lg lg:text-2xl"
            />
          </div>

          <div>
            <label className="block text-[#8696a0] text-sm lg:text-base mb-2 lg:mb-3">Date</label>
            <input
              type="date"
              value={withdrawDate}
              onChange={(e) => {
                setWithdrawDate(e.target.value);
                if (withdrawError) setWithdrawError('');
              }}
              className="w-full bg-[#2a3942] text-white rounded-lg px-4 py-3 lg:px-5 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 border border-[#2a3942]"
            />
          </div>

          {withdrawAmount && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 lg:p-6">
              <p className="text-[#8696a0] text-xs lg:text-sm mb-2">Remaining Balance</p>
              <p className="text-orange-400 text-2xl lg:text-4xl font-medium">
                {formatCurrency(Math.max(0, (selectedInvestment.amount || 0) - parseFloat(withdrawAmount || 0)))}
              </p>
            </div>
          )}

          <button 
            onClick={processWithdrawal} 
            className="w-full bg-orange-500 text-white rounded-lg px-4 py-3 lg:px-6 lg:py-4 text-base lg:text-lg font-medium active:bg-orange-600 hover:bg-orange-600 transition-colors mt-6"
          >
            Confirm Withdrawal
          </button>
        </div>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-[#0b141a]">
      <main className="max-w-7xl mx-auto pb-20 px-4 lg:px-8">

        {/* Header Section */}
        <div className="bg-[#1f2c34] -mx-4 lg:mx-0 lg:rounded-lg lg:mt-6 px-4 lg:px-6 py-4 lg:py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 lg:gap-4 flex-1">
              <button
                onClick={() => navigate('/')}
                className="text-[#8696a0] p-1 -ml-1 active:bg-[#2a3942] hover:bg-[#2a3942] rounded-full transition-colors"
              >
                <ChevronLeft size={24} className="lg:w-6 lg:h-6" />
              </button>
              <div>
                <p className="text-[#8696a0] text-xs lg:text-sm uppercase tracking-wide mb-1">Total Assets</p>
                <p className="text-white text-xl lg:text-3xl font-light">{formatCurrency(getTotalBalance())}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setAddError('');
                setActiveView('add');
              }}
              className="bg-[#00a884] text-white w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors shadow-lg"
            >
              <Plus size={24} className="lg:w-6 lg:h-6" />
            </button>
          </div>
        </div>

        {/* Assets List */}
        <div className="pt-4 lg:pt-6">
          {investments.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
              {investments.map((inv) => (
                <div key={inv.id} className="bg-[#1f2c34] rounded-lg overflow-hidden hover:bg-[#253640] transition-colors">
                  {/* Main Info */}
                  <div className="p-4 lg:p-5">
                    <div className="flex items-start justify-between mb-2 lg:mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs lg:text-sm text-[#00a884] bg-[#00a884]/10 px-2 py-1 rounded">
                            {inv.type}
                          </span>
                        </div>
                        <h3 className="text-white text-base lg:text-lg font-normal mb-2">{inv.name}</h3>
                        <p className="text-[#00a884] text-lg lg:text-xl font-medium">{formatCurrency(inv.amount)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs lg:text-sm text-[#8696a0] pt-3 border-t border-[#2a3942]">
                      <span>Started {inv.date}</span>
                      <span className="flex items-center gap-1">
                        <History size={12} className="lg:w-4 lg:h-4" />
                        {getInvestmentTransactions(inv.id).length} transactions
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-4 border-t border-[#2a3942]">
                    <button
                      onClick={() => openTopUpForm(inv)}
                      className="py-3 lg:py-4 flex flex-col items-center gap-1 text-[#00a884] active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors"
                    >
                      <ArrowUpCircle size={18} className="lg:w-5 lg:h-5" />
                      <span className="text-[10px] lg:text-xs">Top Up</span>
                    </button>
                    <button
                      onClick={() => openWithdrawForm(inv)}
                      className="py-3 lg:py-4 flex flex-col items-center gap-1 text-orange-400 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors border-l border-[#2a3942]"
                    >
                      <ArrowDownCircle size={18} className="lg:w-5 lg:h-5" />
                      <span className="text-[10px] lg:text-xs">Withdraw</span>
                    </button>
                    <button
                      onClick={() => viewInvestmentDetails(inv)}
                      className="py-3 lg:py-4 flex flex-col items-center gap-1 text-blue-400 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors border-l border-[#2a3942]"
                    >
                      <ChevronRight size={18} className="lg:w-5 lg:h-5" />
                      <span className="text-[10px] lg:text-xs">Details</span>
                    </button>
                    <button
                      onClick={() => handleDeleteInvestment(inv.id)}
                      className="py-3 lg:py-4 flex flex-col items-center gap-1 text-red-400 active:bg-[#2a3942] hover:bg-[#2a3942] transition-colors border-l border-[#2a3942]"
                    >
                      <Trash2 size={18} className="lg:w-5 lg:h-5" />
                      <span className="text-[10px] lg:text-xs">Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#1f2c34] rounded-lg p-8 lg:p-16 text-center mt-4 max-w-2xl mx-auto">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#00a884]/10 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                <ArrowUpCircle className="w-8 h-8 lg:w-10 lg:h-10 text-[#00a884]" />
              </div>
              <h3 className="text-white text-base lg:text-2xl font-normal mb-2 lg:mb-3">No Assets Yet</h3>
              <p className="text-[#8696a0] text-sm lg:text-base mb-6 lg:mb-8">Start tracking your investments</p>
              <button
                onClick={() => {
                  setAddError('');
                  setActiveView('add');
                }}
                className="bg-[#00a884] text-white px-6 py-3 lg:px-8 lg:py-4 rounded-full inline-flex items-center gap-2 font-medium text-sm lg:text-base active:bg-[#008f6d] hover:bg-[#008f6d] transition-colors"
              >
                <Plus size={18} className="lg:w-5 lg:h-5" />
                Add First Asset
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}